import { isNitroEnvironment } from 'utils/is-nitro-env';

export type DatadogRumConfig = {
  clientToken: string;
  site: string;
  applicationId: string;
  service: string;
  version: string;
};

type DatadogRum = typeof import('@datadog/browser-rum').datadogRum;

let datadogRum: DatadogRum | null = null;
let initPromise: Promise<DatadogRum | null> | null = null;

export const shouldInitDatadogRum = (): boolean =>
  window.location.hostname.includes('leadsquared') || isNitroEnvironment();

export const initDatadogRumAsync = (
  config: DatadogRumConfig,
): Promise<DatadogRum | null> => {
  if (!shouldInitDatadogRum() || !config.applicationId) {
    return Promise.resolve(null);
  }

  if (datadogRum) {
    return Promise.resolve(datadogRum);
  }

  if (initPromise) {
    return initPromise;
  }

  initPromise = import('@datadog/browser-rum').then((module) => {
    module.datadogRum.init({
      ...config,
      sessionSampleRate: 100,
      sessionReplaySampleRate: 100,
      trackResources: true,
      trackLongTasks: true,
      trackUserInteractions: true,
      excludedActivityUrls: [
        /\/r.lr-in-prod.com$/,
        /\/backend.getbeamer.com$/,
        /\/.pusher.com$/,
      ],
    });
    datadogRum = module.datadogRum;

    return datadogRum;
  });

  return initPromise;
};

export const getDatadogRum = (): Promise<DatadogRum | null> => {
  if (datadogRum) {
    return Promise.resolve(datadogRum);
  }

  if (initPromise) {
    return initPromise;
  }

  return Promise.resolve(null);
};

export const getSessionReplayLink = (): Promise<string | undefined> =>
  getDatadogRum().then((rum) => rum?.getSessionReplayLink());

export const addDatadogRumError = (
  error: Error,
  context?: Record<string, unknown>,
): void => {
  getDatadogRum()
    .then((rum) => {
      rum?.addError(error, context);
    })
    .catch(() => undefined);
};

export const addDatadogRumTiming = (name: string, duration: number): void => {
  getDatadogRum()
    .then((rum) => {
      rum?.addTiming(name, duration);
    })
    .catch(() => undefined);
};

export const setDatadogRumUser = (userInfo: Record<string, unknown>): void => {
  getDatadogRum()
    .then((rum) => {
      rum?.setUser(userInfo);
    })
    .catch(() => undefined);
};
